<template>
  <div
    class="field-group"
    :disabled="objetivos.length > 0 || objetivosEscolhidos.length > 0"
  >
    <label
      for="tipo-obj"
      class="field-label required"
    >
      Objetivos específicos
      <span
        class="help-icon"
        title="Selecione o(s) objetivo(s) específico(s) da atividade"
        >?</span
      >
    </label>

    <select
      class="form-select"
      v-model="objetivoSelecionado"
      @change="adicionarObjetivo"
      required
    >
      <option
        disabled
        hidden
        :value="null"
      >
        Selecione o(s) Objetivo(s) Específico(s) da atividade
      </option>
      <option
        v-for="objetivo in objetivos"
        :key="objetivo.id"
        :value="objetivo"
      >
        {{ objetivo.nome || objetivo.titulo }}
      </option>
    </select>
  </div>

  <div
    class="field-group"
    v-if="
      !isLoading && objetivos.length === 0 && objetivosEscolhidos.length === 0
    "
  >
    <label class="field-label required">
      Objetivos específicos
      <span
        class="help-icon"
        title="Selecione o(s) objetivo(s) específico(s) da atividade"
        >?</span
      >
    </label>
    <div class="objetivos-empty-state">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
        />
      </svg>
      <span>Nenhum objetivo cadastrado para esta ação.</span>
    </div>

    <div
      class="objetivo-geral-checkbox"
      :class="{
        'field-error-box': tentouAvancar && !usarObjetivoGeral,
      }"
    >
      <input
        type="checkbox"
        id="usar-objetivo-geral"
        v-model="usarObjetivoGeral"
      />
      <label for="usar-objetivo-geral">
        Usar objetivo geral <span class="required-asterisk">*</span>
      </label>
    </div>
    <span
      class="error-text"
      v-if="tentouAvancar && !usarObjetivoGeral"
    >
      Você deve marcar "Usar objetivo geral" para continuar.
    </span>
  </div>

  <div
    class="div-objetivos"
    v-show="objetivosEscolhidos.length > 0"
  >
    <h6>Objetivos Selecionados:</h6>
    <div
      class="objetivo"
      v-for="(objetivo, index) in objetivosEscolhidos"
      :key="index"
    >
      <div>{{ objetivo.nome || objetivo.titulo }}</div>
      <button
        class="remover-objetivo"
        @click="removerObjetivo(index)"
      >
        ×
      </button>
    </div>
  </div>
</template>

<script setup>
import { useObjetivos } from "@/composables/useObjetivos";
import { onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

const props = defineProps({
  acaoId: {
    type: String,
    default: "",
  },
  tentouAvancar: {
    type: Boolean,
    default: false,
  },
});

const route = useRoute();
const { objetivos, isLoading, fetchObjetivosAcao } = useObjetivos();

const todosOsObjetivos = ref([]);
const objetivosEscolhidos = ref([]);
const idObjetivosEscolhidos = defineModel("objetivosEscolhidos", {
  default: () => [],
});
const usarObjetivoGeral = defineModel("usarObjetivoGeral", { default: false });
const objetivoSelecionado = ref(null);

function sincronizarObjetivosComIds() {
  const ids = Array.isArray(idObjetivosEscolhidos.value)
    ? idObjetivosEscolhidos.value
    : [];

  if (!todosOsObjetivos.value || todosOsObjetivos.value.length === 0) {
    objetivosEscolhidos.value = [];
    objetivos.value = [];
    return;
  }

  objetivosEscolhidos.value = todosOsObjetivos.value.filter((obj) =>
    ids.includes(obj.id)
  );

  objetivos.value = todosOsObjetivos.value.filter(
    (obj) => !ids.includes(obj.id)
  );
}

function adicionarObjetivo() {
  if (
    !objetivoSelecionado.value ||
    (!objetivoSelecionado.value.titulo && !objetivoSelecionado.value.nome)
  )
    return;

  const obj = objetivoSelecionado.value;
  const currentIds = Array.isArray(idObjetivosEscolhidos.value)
    ? [...idObjetivosEscolhidos.value]
    : [];

  if (!currentIds.includes(obj.id)) {
    currentIds.push(obj.id);
    idObjetivosEscolhidos.value = currentIds;
  }

  objetivoSelecionado.value = null;
  sincronizarObjetivosComIds();
}

function removerObjetivo(index) {
  const objetivo = objetivosEscolhidos.value[index];
  if (!objetivo) return;

  const currentIds = Array.isArray(idObjetivosEscolhidos.value)
    ? [...idObjetivosEscolhidos.value]
    : [];

  idObjetivosEscolhidos.value = currentIds.filter((id) => id !== objetivo.id);
  sincronizarObjetivosComIds();
}

watch(
  () => idObjetivosEscolhidos.value,
  () => {
    sincronizarObjetivosComIds();
  },
  { deep: true }
);

watch(
  () => props.acaoId,
  async (novoAcaoId) => {
    if (novoAcaoId) {
      const res = await fetchObjetivosAcao(novoAcaoId);
      todosOsObjetivos.value = res || objetivos.value || [];
      sincronizarObjetivosComIds();
    }
  }
);

onMounted(async () => {
  const targetAcaoId = props.acaoId || route.params.id;
  if (targetAcaoId) {
    const res = await fetchObjetivosAcao(targetAcaoId);
    todosOsObjetivos.value = res || objetivos.value || [];
    sincronizarObjetivosComIds();
  }
});
</script>

<style scoped>
.div-objetivos {
  display: flex;
  position: relative;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
  border: 1px solid var(--roxo-escuro1);
  border-radius: 15px;
  padding: 20px 5px 10px 10px;
}

.div-objetivos h6 {
  position: absolute;
  margin-top: -30px;
  margin-left: 5px;
  z-index: 101;
  background-color: white;
  color: var(--roxo-escuro1);
  font-size: 15px;
}

.objetivo {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ede9f8;
  color: #5b3fa8;
  border-radius: 12px;
  padding: 3px 10px;
  font-size: 0.8rem;
  font-weight: 500;
}

.remover-objetivo {
  background: none;
  border: none;
  color: #5b3fa8;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s;
}

.remover-objetivo:hover {
  color: #3b2078;
  font-weight: bold;
}
</style>
